import { useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from '@ekhodealer/ekho-common/rev';

import type {
	ConversationListRow,
	ConversationMessage,
	ConversationStatus,
	GetConversationMessagesResponse,
	PaginatedResponse,
} from '@ekhodealer/ekho-common';
import type { InfiniteData } from '@tanstack/react-query';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { conversationsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

interface ResolveConversationParams {
	conversationId: string;
	leadId: string;
}

export interface ResolveConversationResponse {
	conversationId: string;
	status: ConversationStatus;
	dateResolved: string;
	systemMessage: ConversationMessage;
}

const resolveConversation = async (
	servicesProviderId: string,
	conversationId: string,
): Promise<ResolveConversationResponse> => {
	const response = await apiClient.post<ResolveConversationResponse>(
		conversationsEndpoints.resolve(servicesProviderId, conversationId),
	);
	return response.data;
};

export const useResolveConversation = (): {
	mutate: (
		params: ResolveConversationParams,
		options?: { onSuccess?: (data: ResolveConversationResponse) => void },
	) => void;
	isPending: boolean;
} => {
	const queryClient = useQueryClient();
	const servicesProviderId = useCurrentServicesProviderId();

	const mutation = useMutation({
		mutationFn: ({ conversationId }: ResolveConversationParams) =>
			resolveConversation(servicesProviderId, conversationId),
		onSuccess: (data, variables) => {
			toast({
				message: 'Conversation resolved',
				variant: 'success',
			});

			// Update the conversation in all cached list queries using server response
			queryClient.setQueriesData<InfiniteData<PaginatedResponse<ConversationListRow>>>(
				{ queryKey: queries.conversations.list._def },
				(oldData) => {
					if (!oldData?.pages) return oldData;
					return {
						...oldData,
						pages: oldData.pages.map((page) => ({
							...page,
							data: page.data.map((conv) =>
								conv.conversationId === data.conversationId
									? { ...conv, status: data.status, dateResolved: data.dateResolved }
									: conv,
							),
						})),
					};
				},
			);

			// Append the system message to the conversation messages cache
			const messagesQueryKey = queries.conversations.messages(
				servicesProviderId,
				variables.leadId,
				data.conversationId,
			).queryKey;

			queryClient.setQueryData<InfiniteData<GetConversationMessagesResponse>>(
				messagesQueryKey,
				(oldData) => {
					if (!oldData?.pages?.length) return oldData;
					return {
						...oldData,
						pages: oldData.pages.map((page, index) =>
							index === 0
								? { ...page, messages: [data.systemMessage, ...page.messages] }
								: page,
						),
					};
				},
			);

			// Refetch stats since counts changed
			queryClient.invalidateQueries({
				queryKey: queries.conversations.stats._def,
			});
		},
		onError: (error) => {
			toast({
				message: `Failed to resolve conversation: ${(error as Error).message}`,
				variant: 'error',
			});
		},
	});

	return {
		mutate: mutation.mutate,
		isPending: mutation.isPending,
	};
};
